/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { useSearchParams, useNavigate } from "react-router-dom";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import authorsTableData from "layouts/tables/data/authorsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
import { Typography, Box, Tooltip } from "@mui/material";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { FormControlLabel, Switch } from "@mui/material";

import ConstructionIcon from "@mui/icons-material/Construction";
const label = { inputProps: { "aria-label": "Switch demo" } };
import {
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  FormControl,
  InputLabel,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";
import { trackSubscriptionSuccess } from "utils/analytics";
import { useEffect, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { userSettings } from "service/axiosConfig";
import { updateCallSettings } from "service/axiosConfig";
import { useSelector } from "react-redux";
import { callTopics } from "service/axiosConfig";
import { callTime } from "service/axiosConfig";
import { update } from "service/axiosConfig";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

const timezones = [
  { label: "Eastern Time (US & Canada)", id: "America/New_York" },
  { label: "Central Time (US & Canada)", id: "America/Chicago" },
  { label: "Mountain Time (US & Canada)", id: "America/Denver" },
  { label: "Arizona Time (No DST)", id: "America/Phoenix" },
  { label: "Pacific Time (US & Canada)", id: "America/Los_Angeles" },
  { label: "Alaska Time", id: "America/Anchorage" },
  { label: "Hawaii Time", id: "Pacific/Honolulu" },
];
function Tables() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const allowedTopics = [1, 4, 3];
  const plan = typeof window !== "undefined" ? localStorage.getItem("verifiedUserPlan") : "0";
  const isRestrictedPlan =
    localStorage.getItem("verifiedUserPlan") === "1" ||
    localStorage.getItem("verifiedUserPlan") === "0";
  let verifiedUserPlan = localStorage.getItem("verifiedUserPlan");
  const userID = useSelector((state) => state.auth.userId);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const planName = localStorage.getItem("GA_SelectedPlanName");
    const planPrice = parseFloat(localStorage.getItem("GA_SelectedPlanPrice")) || 0;

    if (sessionId && planName) {
      // ✅ Fire GA purchase event
      trackSubscriptionSuccess(sessionId, planName, planPrice);

      // 🧹 Clean up GA-specific storage (no page navigation)
      localStorage.removeItem("GA_SelectedPlanName");
      localStorage.removeItem("GA_SelectedPlanPrice");
    }
  }, [searchParams]);
  const [formData, setFormData] = useState({
    callTime: "",
    callDay: "",
    timeZone: "",
    topics: [],
  });

  const [callPaused, setCallPaused] = useState(false);
  const handlePauseToggle = async (event) => {
    var planStatus = localStorage.getItem("planStatus");
    if (planStatus.toLowerCase() === "expired") {
      toast.error("Your subscription has expired. Please upgrade to continue.");
      navigate("/plans"); // optional
      return;
    }
    if (planStatus.toLowerCase() === "noplan" || verifiedUserPlan === "0") {
      toast.error("Please select a Heartline plan to continue.");
      navigate("/plans"); // optional
      return;
    }
    const isPaused = event.target.checked;
    const userId = parseInt(localStorage.getItem("userId"), 10);
    setCallPaused(isPaused);

    try {
      await updateCallSettings(userId, isPaused);
    } catch (err) {
      toast.error("Could not update call settings!");
    }
  };
  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const [time, setTime] = useState([]);
  const [topics, setTopics] = useState([]);
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const userId = localStorage.getItem("userId");
        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        const sessionId = new URLSearchParams(window.location.search).get("session_id");

        if (sessionId) {
          // wait 2 seconds to give webhook time
          await delay(3000);
        }

        const [timeRes, topicsRes, settingsRes] = await Promise.all([
          callTime(),
          callTopics(),
          userSettings(userId),
        ]);

        console.log(timeRes, settingsRes);
        if (timeRes) setTime(timeRes);
        if (topicsRes) setTopics(topicsRes);
        if (settingsRes) {
          const {
            callTimeOptionId,
            frequencyLabel,
            timeZone,
            userTopics,
            callStatus,
            plan,
            planStatus,
          } = settingsRes;
          setCallPaused(callStatus);
          //console.warn("topics:", userTopics);
          verifiedUserPlan = plan;
          localStorage.setItem("verifiedUserPlan", plan);
          localStorage.setItem("planStatus", planStatus);
          setFormData({
            callTime: callTimeOptionId,
            callDay: frequencyLabel ? frequencyLabel.split(" / ").map((d) => d.trim()) : [],
            timeZone: timeZone,
            topics: userTopics ? normalizeTopics(userTopics) : [null, null, null],
          });
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        console.error(err);
      }
    };

    fetchAll();
  }, []);
  const weekDayOrder = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const sortDays = (days) => {
    return [...days].sort((a, b) => weekDayOrder.indexOf(a) - weekDayOrder.indexOf(b));
  };

  const handleDaySelectChange = (event) => {
    const value = event.target.value.map((v) => String(v));
    const alreadySelected = formData.callDay;

    const isAdding = value.length > alreadySelected.length;

    if (verifiedUserPlan === "0") {
      toast("Upgrade to a paid plan to customize your call days.");
      return;
    }
    // if (verifiedUserPlan === "2" && isAdding && value.length > 5) {
    //   toast("You can select up to 5 days only.");
    //   return;
    // }
    // if (verifiedUserPlan !== "3" && verifiedUserPlan !== "2" && isAdding && value.length > 3)
    // All others except Plan 3: max 3 days
    if (verifiedUserPlan !== "3" && isAdding && value.length > 3) {
      toast("You can select up to 3 days only.");
      return;
    }

    handleSelectChange("callDay", value);
  };
  const predefinedOptions = {
    1: ["Monday", "Wednesday", "Friday"],
    2: ["Tuesday", "Thursday"],
    3: ["Saturday", "Sunday"],
  };

  const getCallDayId = (selectedDays) => {
    const normalized = selectedDays.slice().sort().join(" / ");

    for (const [id, days] of Object.entries(predefinedOptions)) {
      const preset = days.slice().sort().join(" / ");
      if (preset === normalized) {
        return parseInt(id);
      }
    }

    return 4; // Custom
  };

  const normalizeTopics = (topicsArray) => {
    const normalized = [...topicsArray];
    while (normalized.length < 3) {
      normalized.push(null);
    }
    return normalized.slice(0, 3); // just in case API returns more than 3
  };

  const handleUpdate = async () => {
    var planStatus = localStorage.getItem("planStatus");
    if (planStatus.toLowerCase() === "expired") {
      toast.error("Your subscription has expired. Please upgrade to continue.");
      navigate("/plans"); // optional
      return;
    }
    if (planStatus.toLowerCase() === "noplan" || verifiedUserPlan === "0") {
      toast.error("Please select a Heartline plan to continue.");
      navigate("/plans"); // optional
      return;
    }

    const { callDay, callTime, timeZone, topics } = formData;
    const cleanTopics = topics.filter((id) => id !== null);

    if (!callDay || !callTime || !timeZone) {
      if (!callDay) toast("Please select a call day");
      if (!callTime) toast("Please select a call time");
      if (!timeZone) toast("Please select a time zone");
      return;
    }
    // Validate topic selection
    if (!cleanTopics || cleanTopics.length === 0) {
      toast("Please select 3 topics to continue");
      return;
    }
    if (cleanTopics.length != 3) {
      toast("Please select  3 topics to continue");
      return;
    }
    if (cleanTopics.length > 3) {
      toast("You can select a maximum of 3 topics");
      return;
    }
    try {
      const userId = (await localStorage.getItem("userId")) || userID;
      const callDayId = getCallDayId(formData.callDay);
      await update({
        userId,
        callDay: callDayId, // Send static ID to indicate "Custom"
        callTime,
        timeZoneId: timeZone,
        callFrequencyOption: sortDays(formData.callDay).join(" / "),
        userTopics: cleanTopics, // Join selected days with " / "
      });
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  return (
    <>
      <header
        style={{
          backgroundColor: "#561919",
          color: "#fff",
          padding: "1rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
        <div>
          <Link
            to="/"
            style={{
              fontWeight: "bold",
              fontSize: "1.25rem",
              color: "#fff",
              textDecoration: "underline",
            }}
          >
            Heartline
          </Link>
        </div>

        {/* Right: Nav Items */}
        {/* <nav style={{ display: "flex", gap: "2rem" }}>
          <Link
            to="/contact"
            style={{
              textDecoration: "none",
              color: "#fff",
              fontWeight: "bold",
            }}
          >
            Contact
          </Link>
          <Link
            to="/authentication/demo"
            style={{
              textDecoration: "none",
              color: "#fff",
              fontWeight: "bold",
            }}
          >
            Demo
          </Link>
        </nav> */}
      </header>
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant=""
                  bgColor="rgb(86,25,25)"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center" // Optional: vertically center items
                >
                  <MDBox variant="" bgColor="rgb(86,25,25)" borderRadius="lg" display="flex">
                    <ConstructionIcon fontSize="medium" color="white" />
                    <MDTypography variant="h6" color="white" px={1}>
                      Current Settings
                    </MDTypography>
                  </MDBox>

                  <DashboardNavbar />
                </MDBox>
                {loading ? (
                  <MDBox display="flex" alignItems="center" justifyContent="center" height="200px">
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <MDBox mx={2} sx={{ pt: 2.5 }}>
                    {/* Top-level grid: Left (Form) / Right (Topics). Stacks on mobile */}
                    <Grid container spacing={{ xs: 3, md: 4 }}>
                      {/* 🟦 Left: Form Section */}
                      <Grid item xs={12} md={5}>
                        {/* Row: CALL TIME */}
                        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                              CALL TIME
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <FormControl fullWidth>
                              <Select
                                value={formData.callTime}
                                onChange={(e) => handleSelectChange("callTime", e.target.value)}
                                displayEmpty
                                IconComponent={ArrowDropDownIcon}
                                sx={{ height: "2.75rem" }}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 48 * 3.5, // 5 items (5.5 for spacing)
                                      width: 250, // optional fixed width
                                    },
                                  },
                                }}
                              >
                                <MenuItem value="" disabled>
                                  Select Call Time
                                </MenuItem>
                                {time?.map((item) => (
                                  <MenuItem key={item.id} value={item.id}>
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>

                        {/* Row: CALL FREQUENCY */}
                        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                              CALL FREQUENCY
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <FormControl fullWidth>
                              <Select
                                multiple
                                displayEmpty
                                value={Array.isArray(formData.callDay) ? formData.callDay : []}
                                onChange={handleDaySelectChange}
                                renderValue={(selected) => {
                                  if (!selected || selected.length === 0) {
                                    return (
                                      <span style={{ color: "#9e9e9e" }}>Select Call Days</span>
                                    );
                                  }
                                  // Shorten day names if 4 or more selected
                                  const displayDays =
                                    selected.length >= 4
                                      ? selected.map((day) => day.slice(0, 3)) // Mon, Tue, Wed
                                      : selected;

                                  return sortDays(displayDays).join(" / ");
                                }}
                                IconComponent={ArrowDropDownIcon}
                                sx={{ minHeight: "2.75rem" }}
                              >
                                {[
                                  "Monday",
                                  "Tuesday",
                                  "Wednesday",
                                  "Thursday",
                                  "Friday",
                                  "Saturday",
                                  "Sunday",
                                ].map((day) => (
                                  <MenuItem key={day} value={day}>
                                    <Checkbox
                                      checked={formData.callDay.includes(String(day).trim())}
                                      color="default"
                                      sx={{
                                        color: "rgb(86,25,25)",
                                        "&.Mui-checked": { color: "rgb(86,25,25)" },
                                        "& .MuiSvgIcon-root": {
                                          fontSize: 18,
                                          color: "rgb(86,25,25)",
                                        },
                                        p: "4px",
                                      }}
                                    />
                                    <ListItemText
                                      primary={day}
                                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                                      sx={{ ml: 1 }}
                                    />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>

                        {/* Row: TIMEZONE */}
                        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>TIMEZONE</Typography>
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <FormControl fullWidth>
                              <Select
                                value={formData.timeZone}
                                onChange={(e) => handleSelectChange("timeZone", e.target.value)}
                                displayEmpty
                                IconComponent={ArrowDropDownIcon}
                                sx={{ height: "2.75rem" }}
                              >
                                <MenuItem value="" disabled>
                                  Select Timezone
                                </MenuItem>
                                {timezones?.map((item) => (
                                  <MenuItem key={item.id} value={item.id}>
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>

                        {/* Row: PAUSE CALLS */}
                        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                              PAUSE CALLS
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Typography
                                fontWeight="bold"
                                sx={{
                                  color: callPaused ? "#561919" : "#A1A1A1",
                                  backgroundColor: callPaused ? "#fdf3f3" : "transparent",
                                  px: 1.2,
                                  py: 0.2,
                                  borderRadius: 1,
                                  fontSize: "0.85rem",
                                  transition: "all 0.3s ease",
                                }}
                              >
                                {callPaused ? "ON" : "OFF"}
                              </Typography>
                              <Switch
                                checked={callPaused}
                                onChange={handlePauseToggle}
                                disableRipple
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: "#34c759",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                                    border: "none",
                                  },
                                }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* 🟥 Right: Topics Section */}
                      <Grid item xs={12} md={7}>
                        {/* CURRENT TOPICS */}
                        <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={3} mt={0.4}>
                            <Typography
                              sx={{ fontSize: 14, fontWeight: 600, pt: { xs: 0.5, sm: 0.5 } }}
                            >
                              CURRENT TOPICS
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={9}>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "repeat(2, minmax(0, 1fr))",
                                  sm: "repeat(3, minmax(0, 1fr))",
                                },
                                gap: 1,
                                minWidth: { sm: 0 },
                                maxWidth: { sm: "100%" },
                              }}
                            >
                              {formData.topics.map((topicId, slotIndex) => {
                                const topic = topics.find((t) => t.id === topicId);
                                return (
                                  <Box key={slotIndex} sx={{ height: 32 }}>
                                    {topic ? (
                                      <Tooltip
                                        title={topic.tooltip || topic.name}
                                        placement="top"
                                        arrow
                                        componentsProps={{
                                          tooltip: {
                                            sx: { fontSize: 11, maxWidth: 200, p: "6px 10px" },
                                            arrow: { color: "#fff" },
                                          },
                                        }}
                                      >
                                        <span>
                                          <Chip
                                            label={topic.name}
                                            onDelete={() => {
                                              setFormData((prev) => {
                                                const updated = [...prev.topics];
                                                updated[slotIndex] = null;
                                                return { ...prev, topics: updated };
                                              });
                                            }}
                                            sx={{
                                              backgroundColor: "rgb(86,25,25)",
                                              color: "#fff",
                                              fontSize: 11,
                                              borderRadius: "6px",
                                              height: 32,
                                              pr: "3px",
                                              width: { xs: "128px", sm: "128px", md: "256px" },
                                              whiteSpace: "nowrap",
                                              ".MuiChip-deleteIcon": {
                                                color: "#fff",
                                                fontSize: "10.5px",
                                                mr: "2px",
                                                "&:hover": { color: "#ccc" },
                                              },
                                            }}
                                          />
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <Box sx={{ width: "100%", height: "100%" }} />
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Grid>
                        </Grid>

                        {/* SELECT TOPICS */}
                        <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={3} mt={7}>
                            <Typography
                              sx={{ fontSize: 14, fontWeight: 600, pt: { xs: 0, sm: 3.4 } }}
                            >
                              SELECT TOPICS
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={9}>
                            <Box
                              sx={{
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                p: 2,
                                maxHeight: { xs: 200, sm: 200 },
                                overflowY: "auto",
                                overflowX: "hidden",
                                backgroundColor: "#fafafa",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: {
                                    xs: "repeat(2, minmax(0, 1fr))",
                                    sm: "repeat(3, minmax(0, 1fr))",
                                  },
                                  gap: 1.5,
                                }}
                              >
                                {topics?.map((item) => {
                                  const selected = formData.topics.includes(item.id);
                                  const limitReached = !formData.topics.includes(null); // full if no empty slot
                                  const isAllowedTopic = allowedTopics.includes(item.id);
                                  const isRestrictedPlan = plan === "1" || plan === "0";

                                  return (
                                    <Tooltip
                                      key={item.id}
                                      title={item.tooltip || item.name}
                                      placement="top"
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: { fontSize: 11, maxWidth: 200, p: "6px 10px" },
                                          arrow: { color: selected ? "rgb(86,25,25)" : "#f0f0f0" },
                                        },
                                      }}
                                    >
                                      <span>
                                        <Chip
                                          label={item.name}
                                          clickable
                                          onClick={() => {
                                            if (isRestrictedPlan && !isAllowedTopic) {
                                              toast?.info(
                                                "Upgrade to Plus or Premium to select more topics."
                                              );
                                              return;
                                            }

                                            if (selected) {
                                              // Unselect by replacing with null
                                              setFormData((prev) => {
                                                const updated = [...prev.topics];
                                                const indexToRemove = updated.findIndex(
                                                  (id) => id === item.id
                                                );
                                                if (indexToRemove !== -1)
                                                  updated[indexToRemove] = null;
                                                return { ...prev, topics: updated };
                                              });
                                            } else if (limitReached) {
                                              toast?.("You can select maximum 3 topics");
                                            } else {
                                              // Add into first empty slot
                                              setFormData((prev) => {
                                                const updated = [...prev.topics];
                                                const emptyIndex = updated.findIndex(
                                                  (id) => id === null
                                                );
                                                if (emptyIndex !== -1)
                                                  updated[emptyIndex] = item.id;
                                                return { ...prev, topics: updated };
                                              });
                                            }
                                          }}
                                          sx={{
                                            backgroundColor: selected ? "rgb(86,25,25)" : "#f0f0f0",
                                            color: selected ? "#fff" : "#000",
                                            fontSize: 11,
                                            borderRadius: "4px",
                                            height: 24,
                                            width: "128px",
                                            lineHeight: "24px",
                                            justifyContent: "center",
                                            opacity:
                                              (isRestrictedPlan && !isAllowedTopic) ||
                                              (!selected && limitReached)
                                                ? 0.6
                                                : 1,
                                            cursor:
                                              (isRestrictedPlan && !isAllowedTopic) ||
                                              (!selected && limitReached)
                                                ? "not-allowed"
                                                : "pointer",
                                          }}
                                        />
                                      </span>
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* 🔘 Centered Update Button */}
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                      <Button
                        onClick={handleUpdate}
                        sx={{
                          backgroundColor: "#561919",
                          color: "#fff",
                          border: "2px solid #561919",
                          mx: { xs: 0, sm: "22px" },
                          my: "10px",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          p: "12px",
                          width: { xs: "100%", sm: "20%" },
                          "&:hover": {
                            backgroundColor: "#f5f5f5 !important",
                            color: "#561919",
                          },
                          "&:disabled": {
                            boxShadow: "none",
                            color: "#561919 !important",
                          },
                          "& *": {
                            color: "#561919 !important",
                          },
                        }}
                      >
                        Update Settings
                      </Button>
                    </Box>
                  </MDBox>
                )}
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    </>
  );
}

export default Tables;
